import { useNavigate, useLocation, useLoaderData, useRevalidator } from 'react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import { useEffect, useState } from 'react';
import Page from 'client/components/aria/Page/Page';
import SortButton from 'client/components/aria/SortButton/SortButton';
import Table from 'client/components/aria/Table';
import { Cell, Column, Item, Row, TableBody, TableHeader } from 'react-stately';
import Menu from 'client/components/aria/Menu/Menu';
import Pagination from 'client/components/aria/Pagination/Pagination';
import ShowResults from 'client/components/aria/ShowResults/ShowResults';
import Alert from 'client/components/aria/Alert/Alert';
// import Search from 'client/components/aria/Search/Search';
import moment from 'moment';
import Icon from '@mdi/react';
import { mdiDotsVertical } from '@mdi/js';
import { logWithPrefix } from 'client/utils/logging';

import { MAP_PAGE, VIEW_WORKPACK_PAGE } from 'client/utils/routes/routeConstants';
import styles from './ListWorkpackPage.module.scss';
import { WorkpackService } from 'client/services';
import NavLink from 'client/components/aria/NavLink/NavLink';
import WorkpackStatus from '../components/WorkpackStatus/WorkpackStatus';
import WorkpackDownloadButton from '../components/WorkpackDownloadButton/WorkpackDownloadButton';
import { getLatestWorkpack, getWorkpackStatus } from './utils/workpackUtils';
import { WORKPACK_DISPLAY_STATUS } from './utils/workpackStatusConstants';
import WorkpackAlertModal from '../components/WorkpackAlerts/WorkpackAlertModal/WorkpackAlertModal';
import { VisuallyHidden } from 'react-aria';
import { usePollWorkpacks } from './usePollWorkpacks';
import { useWorkpackDownload } from '../hooks/useWorkpackDownload';

const log = logWithPrefix('ListWorkpackPage');

const MENU_ACTION = {
    VIEW_WORKPACK: 'VIEW_WORKPACK',
    VIEW_ON_MAP: 'VIEW_ON_MAP',
    DELETE: 'DELETE'
};

const ListWorkpackPage = () => {
    const intl = useIntl();
    const { workpackRequests } = useLoaderData();
    const { content = [], totalPages = 0 } = workpackRequests || {};

    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [workpackToDelete, setWorkpackToDelete] = useState(null);
    const { downloadErrorMessage, downloadWorkpack } = useWorkpackDownload('listWorkpackPage');

    const navigate = useNavigate();
    const location = useLocation();
    const { revalidate } = useRevalidator();
    usePollWorkpacks({ workpackRequests, revalidate });

    const params = new URLSearchParams(location.search);
    const currentPage = parseInt(params.get('page')) + 1 || 1;
    const [deleteSuccessMessage, setDeleteSuccessMessage] = useState('');

    useEffect(() => {
        let timeoutId;
        if (deleteSuccessMessage) {
            timeoutId = setTimeout(() => {
                setDeleteSuccessMessage('');
            }, 5000);
        }
        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [deleteSuccessMessage]);

    const onTableMenuAction = async (evt, workpackRequest) => {
        if (evt === MENU_ACTION.VIEW_ON_MAP) {
            navigate(MAP_PAGE + '?workpack=' + workpackRequest.workpackReqId);
        }
        if (evt === MENU_ACTION.VIEW_WORKPACK) {
            navigate(VIEW_WORKPACK_PAGE + '/' + workpackRequest.workpackReqId);
        }
        if (evt === MENU_ACTION.DELETE) {
            setWorkpackToDelete(workpackRequest);
            setShowDeleteDialog(true);
        }
    };

    const handleDownload = (workpackRequest) => {
        const pack = getLatestWorkpack(workpackRequest.workpacks);
        return downloadWorkpack(workpackRequest.workpackReqId, pack, workpackRequest.title);
    };

    const handleConfirmDelete = async () => {
        if (!workpackToDelete) {
            return;
        }

        try {
            await WorkpackService.deleteWorkpackRequests([workpackToDelete.workpackReqId]);
            setDeleteSuccessMessage(intl.formatMessage({ id: 'listWorkpackPage.delete.success' }, { title: workpackToDelete.title }));
            setShowDeleteDialog(false);
            setWorkpackToDelete(null);
            await revalidate();
        } catch (error) {
            log('Failed to delete workpack', { workpackReqId: workpackToDelete.workpackReqId, error });
            setShowDeleteDialog(false);
            setWorkpackToDelete(null);
        }
    };

    const onPaginationChange = page => {
        const params = new URLSearchParams(location.search);
        params.set('page', page - 1);
        navigate(location.pathname + '?' + params.toString());
    };

    return (
        <Page
            title={intl.formatMessage({ id: 'listWorkpackPage.page.title' })}
            role="main"
            data-testid="listWorkpackPage"
            className={styles.page}
        >
            <div className="govuk-grid-row">
                <div className="govuk-grid-column-full">
                    {deleteSuccessMessage && (
                        <Alert
                            variant="warning"
                            title={intl.formatMessage({ id: 'listWorkpackPage.delete.success.title' })}
                            data-testid="list-workpack-delete-success-alert"
                        >
                            <p className="govuk-body">{deleteSuccessMessage}</p>
                        </Alert>
                    )}
                    {downloadErrorMessage && (
                        <Alert
                            variant="error"
                            title={intl.formatMessage({ id: 'listWorkpackPage.download.error.title' })}
                            data-testid="list-workpack-download-error-alert"
                        >
                            <p className="govuk-body">{downloadErrorMessage}</p>
                        </Alert>
                    )}
                    <h1 data-testid="page.title.workpacks" className={['govuk-heading-xl', styles.header].join(' ')}>{intl.formatMessage({ id: 'listWorkpackPage.page.title' })}</h1>
                    <hr className="govuk-section-break govuk-section-break--l govuk-section-break--visible" />
                </div>
            </div>
            <div className={'govuk-grid-row'}>
                <div className="govuk-grid-column-full">
                    {/* TODO: Re-enable search when backend supports it - TASK 13308*
                    <Search
                        data-testid='work'
                        totalElements={loaderData.workpackRequests.totalElements}
                        size={loaderData.workpackRequests.size}
                    /> */}
                </div>
                <div className={['govuk-grid-column-full', styles.table].join(' ')}>
                    <Table
                        aria-label={intl.formatMessage({ id: 'listWorkpackPage.page.title' })}
                        data-testid="listWorkpackPage"
                    >
                        <TableHeader>
                            <Column>
                                <SortButton column={'title'}>
                                    {intl.formatMessage({ id: 'name' })}
                                </SortButton>
                            </Column>
                            <Column>
                                <SortButton column={'dateCreated'}>
                                    {intl.formatMessage({ id: 'dateCreated' })}
                                </SortButton>
                            </Column>
                            <Column>
                                {intl.formatMessage({ id: 'listWorkpackPage.status' })}
                            </Column>
                            {/* <Column>
                                {intl.formatMessage({ id: 'listWorkpackPage.purpose' })}
                            </Column> */}
                            <Column>
                                <VisuallyHidden><FormattedMessage id="listWorkpackPage.actions" /></VisuallyHidden>
                            </Column>
                        </TableHeader>
                        <TableBody>
                            {content.map(workpackRequest => {
                                const workpackStatus = getWorkpackStatus(workpackRequest.workpacks, workpackRequest.dateCreated);
                                const { displayStatus } = workpackStatus;
                                const isDownloadable = displayStatus === WORKPACK_DISPLAY_STATUS.NEW_TODAY ||
                                    displayStatus === WORKPACK_DISPLAY_STATUS.AVAILABLE ||
                                    displayStatus === WORKPACK_DISPLAY_STATUS.AVAILABLE_DAYS_LEFT;
                                const showDelete = displayStatus !== WORKPACK_DISPLAY_STATUS.CREATING;

                                return <Row key={workpackRequest.workpackReqId}>
                                    <Cell wrapText>
                                        <NavLink data-testid='listWorkpackPage.link' to={VIEW_WORKPACK_PAGE + '/' + workpackRequest.workpackReqId}>{workpackRequest.title}</NavLink>
                                    </Cell>
                                    <Cell data-testid={`list-workpack-row-${workpackRequest.workpackReqId}-date-created`}>{moment(workpackRequest.dateCreated).format('D MMM YYYY')}</Cell>
                                    <Cell>
                                        <WorkpackStatus
                                            displayStatus={workpackStatus.displayStatus}
                                            daysRemaining={workpackStatus.daysRemaining}
                                            data-testid={`list-workpack-row-${workpackRequest.workpackReqId}-status`}
                                        />
                                    </Cell>
                                    {/* <Cell data-testid={`list-workpack-row-${workpackRequest.workpackReqId}-purpose`}>{getPurposeLabel(workpackRequest.purpose)}</Cell> */}
                                    <Cell horzAlign="middle">
                                        <div className={styles.actionsCell}>
                                            {isDownloadable && (
                                                <WorkpackDownloadButton
                                                    iconOnly
                                                    variant={displayStatus === WORKPACK_DISPLAY_STATUS.NEW_TODAY ? 'info' : 'secondary'}
                                                    onPress={() => handleDownload(workpackRequest)}
                                                    tooltip={intl.formatMessage({ id: 'listWorkpackPage.download' })}
                                                    data-testid={`list-workpack-row-${workpackRequest.workpackReqId}-download`}
                                                    aria-label={intl.formatMessage({ id: 'listWorkpackPage.download' })}
                                                >
                                                    {intl.formatMessage({ id: 'listWorkpackPage.download' })}
                                                </WorkpackDownloadButton>
                                            )}
                                            <Menu
                                                aria-label={`${workpackRequest.title} ${intl.formatMessage({ id: 'menu' })}`}
                                                icon={<Icon path={mdiDotsVertical} size={1} />}
                                                onAction={evt => onTableMenuAction(evt, workpackRequest)}
                                                placement="bottom right"
                                                data-testid={`list-workpack-row-${workpackRequest.workpackReqId}-menu`}
                                            >
                                                <Item
                                                    key={MENU_ACTION.VIEW_ON_MAP}
                                                    aria-label={intl.formatMessage({ id: 'listWorkpackPage.viewOnMap' })}
                                                >
                                                    {intl.formatMessage({ id: 'listWorkpackPage.viewOnMap' })}
                                                </Item>
                                                {showDelete && (
                                                    <Item
                                                        key={MENU_ACTION.DELETE}
                                                        aria-label={intl.formatMessage({ id: 'listWorkpackPage.delete' })}
                                                        variant="destructive"
                                                        className="deleteMenuItem"
                                                    >
                                                        {intl.formatMessage({ id: 'listWorkpackPage.delete' })}
                                                    </Item>
                                                )}
                                            </Menu>
                                        </div>
                                    </Cell>
                                </Row>;
                            })}
                        </TableBody>
                    </Table>
                </div>
            </div>
            <div className="govuk-grid-row">
                <div className={['govuk-grid-column-full', styles.pagination].join(' ')}>
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onChange={onPaginationChange}
                        data-testid="pagination"
                    />
                </div>
                <div className="govuk-grid-column-full govuk-!-text-align-centre">
                    <ShowResults
                        totalElements={workpackRequests?.totalElements ?? content.length}
                        size={workpackRequests?.size ?? content.length}
                    />
                </div>
            </div>
            <WorkpackAlertModal
                showWarningDialog={showDeleteDialog}
                setShowWarningDialog={setShowDeleteDialog}
                onConfirm={handleConfirmDelete}
                workpackTitle={workpackToDelete?.title}
            />
        </Page>
    );
};

export default ListWorkpackPage;
